import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import { interpolateI18n } from '../../utils/interpolateI18n';
import { formatIncomeYen } from '../spouseSection/spouseEligibility';
import { isMarried, shouldShowStepInHistory, stepQuestionKey } from './specialStatusSteps';
import type { SpecialStatusFlow } from './useSpecialStatusFlow';
import type { SpecialStatusStep } from './specialStatusTypes';

const DISPLAY_ORDER: SpecialStatusStep[] = [
  'singleParentIntro',
  'singleParentPickChild',
  'singleParentConfirmChild',
  'singleParentClaimedByOther',
  'singleParentResult',
  'widowIntro',
  'widowLossStatus',
  'widowRemarried',
  'widowResult',
  'workingStudentIntro',
  'workingStudentEnrolled',
  'workingStudentSchoolType',
  'workingStudentSalaryOnly',
  'workingStudentSalaryRevenue',
  'workingStudentNonEmployment',
  'workingStudentNonEmploymentAmount',
  'workingStudentResult',
  'disabilityWsChangedDuringYear',
  'disabilityWsChangeDate',
];

function IncomeNote({ flow, limitKey }: { flow: SpecialStatusFlow; limitKey: string }) {
  if (flow.employeeIncome <= 0) return null;
  return (
    <ProfileAssistantRow>
      {interpolateI18n(flow.t(limitKey), { income: formatIncomeYen(flow.employeeIncome) })}
    </ProfileAssistantRow>
  );
}

function AnswerRow({ flow, fieldStep, children }: { flow: SpecialStatusFlow; fieldStep: SpecialStatusStep; children: React.ReactNode }) {
  return (
    <ProfileUserAnswerRow
      showUndo={flow.canShowUndoForStep(fieldStep)}
      onUndo={() => void flow.handleUndo(fieldStep)}
      undoLabel={flow.undoLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

export default function SpecialStatusChatThread({ flow }: { flow: SpecialStatusFlow }) {
  const { t, snap, maritalStatus, candidates } = flow;

  return (
    <>
      {DISPLAY_ORDER.map((s) => {
        if (
          !shouldShowStepInHistory(
            s,
            flow.isCommitted(s),
            snap,
            maritalStatus,
            candidates.length,
            flow.hasDisabilityContext
          )
        ) {
          return null;
        }

        const question = t(stepQuestionKey(s));
        let extra: React.ReactNode = null;
        if (s === 'singleParentIntro' && flow.usesSingleParentChildLoop) {
          extra = (
            <ProfileAssistantRow>
              {t('submitDocuments.dependentDeduction.specialStatus.singleParent.multiChildNote')}
            </ProfileAssistantRow>
          );
        }
        if (s === 'singleParentResult' && !isMarried(maritalStatus)) {
          extra = (
            <IncomeNote
              flow={flow}
              limitKey="submitDocuments.dependentDeduction.specialStatus.singleParent.incomeNote"
            />
          );
        }
        if (s === 'widowResult' && !isMarried(maritalStatus)) {
          extra = (
            <IncomeNote flow={flow} limitKey="submitDocuments.dependentDeduction.specialStatus.widow.incomeNote" />
          );
        }

        const answer = flow.userAnswerForStep(s);
        if (s.endsWith('Intro')) {
          return (
            <React.Fragment key={s}>
              <ProfileAssistantRow>{question}</ProfileAssistantRow>
              {extra}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={s}>
            <ProfileAssistantRow>{question}</ProfileAssistantRow>
            {extra}
            {answer && flow.hasDisplayableAnswer(s) ? (
              <AnswerRow flow={flow} fieldStep={s}>
                {answer}
              </AnswerRow>
            ) : null}
          </React.Fragment>
        );
      })}

      {flow.usesSingleParentChildLoop &&
        flow.childAttemptHistory.map((entry, idx) => (
          <React.Fragment key={`child-attempt-${entry.candidate.empFamilyId}-${idx}`}>
            <ProfileAssistantRow>
              {interpolateI18n(t('submitDocuments.dependentDeduction.specialStatus.singleParent.childProgress'), {
                current: String(idx + 1),
                total: String(candidates.length),
                name: entry.candidate.fullName,
                relationship: t(`submitDocuments.familyInfo.relationship.${entry.candidate.relationship}`),
              })}
            </ProfileAssistantRow>
            <ProfileAssistantRow>
              {interpolateI18n(
                t('submitDocuments.dependentDeduction.specialStatus.singleParent.confirmChildPrompt'),
                {
                  name: entry.candidate.fullName,
                  relationship: t(`submitDocuments.familyInfo.relationship.${entry.candidate.relationship}`),
                }
              )}
            </ProfileAssistantRow>
            <ProfileUserAnswerRow>{flow.formatChildHistoryAnswer(entry, 'confirmed')}</ProfileUserAnswerRow>
            {entry.claimedByOther != null ? (
              <>
                <ProfileAssistantRow>
                  {interpolateI18n(
                    t('submitDocuments.dependentDeduction.specialStatus.singleParent.claimedByOtherPrompt'),
                    { name: entry.candidate.fullName }
                  )}
                </ProfileAssistantRow>
                <ProfileUserAnswerRow>{flow.formatChildHistoryAnswer(entry, 'claimedByOther')}</ProfileUserAnswerRow>
              </>
            ) : null}
          </React.Fragment>
        ))}

      {flow.reviewMode && flow.step === 'review' && (
        <ProfileAssistantRow>{t('submitDocuments.dependentDeduction.specialStatus.reviewSummary')}</ProfileAssistantRow>
      )}
      {flow.reviewMode && snap.singleParentOutcome === 'eligible' && (
        <ProfileAssistantRow>
          {t('submitDocuments.dependentDeduction.specialStatus.checkbox.singleParent')}
        </ProfileAssistantRow>
      )}
      {flow.reviewMode && snap.widowOutcome === 'eligible' && (
        <ProfileAssistantRow>
          {t('submitDocuments.dependentDeduction.specialStatus.checkbox.widow')}
        </ProfileAssistantRow>
      )}
      {flow.reviewMode && snap.workingStudentOutcome === 'eligible' && (
        <ProfileAssistantRow>
          {t('submitDocuments.dependentDeduction.specialStatus.checkbox.workingStudent')}
        </ProfileAssistantRow>
      )}

      {!flow.reviewMode && !flow.isComplete && flow.step !== 'review' && (
        <>
          {flow.step === 'singleParentConfirmChild' || flow.step === 'singleParentClaimedByOther' ? (
            <>
              {flow.childProgressLabel() ? (
                <ProfileAssistantRow>{flow.childProgressLabel()}</ProfileAssistantRow>
              ) : null}
              <ProfileAssistantRow>
                {flow.step === 'singleParentConfirmChild'
                  ? interpolateI18n(
                      t('submitDocuments.dependentDeduction.specialStatus.singleParent.confirmChildPrompt'),
                      {
                        name: flow.selectedCandidate?.fullName ?? '',
                        relationship: t(
                          `submitDocuments.familyInfo.relationship.${flow.selectedCandidate?.relationship ?? 'child'}`
                        ),
                      }
                    )
                  : interpolateI18n(
                      t('submitDocuments.dependentDeduction.specialStatus.singleParent.claimedByOtherPrompt'),
                      { name: flow.selectedCandidate?.fullName ?? '' }
                    )}
              </ProfileAssistantRow>
            </>
          ) : (
            <ProfileAssistantRow>{t(stepQuestionKey(flow.step))}</ProfileAssistantRow>
          )}
          {flow.step === 'singleParentResult' && !isMarried(maritalStatus) && (
            <IncomeNote
              flow={flow}
              limitKey="submitDocuments.dependentDeduction.specialStatus.singleParent.incomeNote"
            />
          )}
          {flow.step === 'widowResult' && !isMarried(maritalStatus) && (
            <IncomeNote flow={flow} limitKey="submitDocuments.dependentDeduction.specialStatus.widow.incomeNote" />
          )}
          {flow.step.endsWith('Result') && flow.hasDisplayableAnswer(flow.step) ? (
            <AnswerRow flow={flow} fieldStep={flow.step}>
              {flow.userAnswerForStep(flow.step)}
            </AnswerRow>
          ) : null}
        </>
      )}
    </>
  );
}
